import { useActingAccount } from "@/hooks/useSupportSession";
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { deriveHourlyRate } from "@/lib/onboardingBranches";

export interface OrgSettings {
  id: string;
  user_id: string;
  org_id: string | null;
  org_type: string | null;
  org_state: string | null;
  org_scale: number | null;
  acquisition_cost: number | null;
  avg_hourly_rate: number | null;
  avg_annual_salary: number | null;
  annual_budget: number | null;
  primary_goal: string | null;
  branch_answers: Record<string, any>;
  calibrated_at: string | null;
  skipped_at: string | null;
  onboarding_completed_at: string | null;
  onboarding_dismissed_at: string | null;
}

export interface OnboardingPayload {
  org_type: string;
  org_state: string;
  org_scale: number;
  avg_annual_salary: number;
  annual_budget?: number | null;
  primary_goal: string;
  branch_answers: Record<string, any>;
}

export function useOrgSettings() {
  // In support mode this is the supported workspace owner, so their settings load and save.
  const acting = useActingAccount();
  const user = useMemo(() => (acting ? { id: acting.user_id } : null), [acting?.user_id]);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["org-settings", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("org_settings")
        .select("*")
        .eq("user_id", user.id)
        .is("org_id", null)
        .maybeSingle();
      if (error) throw error;
      return data as OrgSettings | null;
    },
    enabled: !!user?.id,
  });

  const saveOnboarding = useMutation({
    mutationFn: async (answers: OnboardingPayload) => {
      if (!user?.id) throw new Error("Not authenticated");
      const derivedHourly = deriveHourlyRate(answers.avg_annual_salary);
      const payload = {
        user_id: user.id,
        org_id: null,
        org_type: answers.org_type,
        org_state: answers.org_state,
        org_scale: answers.org_scale,
        avg_annual_salary: answers.avg_annual_salary,
        avg_hourly_rate: derivedHourly,
        annual_budget: answers.annual_budget ?? null,
        primary_goal: answers.primary_goal,
        branch_answers: answers.branch_answers ?? {},
        onboarding_completed_at: new Date().toISOString(),
        skipped_at: null,
      };
      const { data: existing } = await supabase
        .from("org_settings")
        .select("id")
        .eq("user_id", user.id)
        .is("org_id", null)
        .maybeSingle();
      if (existing?.id) {
        const { error } = await supabase
          .from("org_settings")
          .update(payload)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_settings").insert(payload);
        if (error) throw error;
      }
      const { error: fnErr } = await supabase.functions.invoke(
        "calibrate-org-multipliers",
        { body: { org_id: null } },
      );
      if (fnErr) throw fnErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-settings"] });
      qc.invalidateQueries({ queryKey: ["org-multipliers"] });
      qc.invalidateQueries({ queryKey: ["cycle-roi"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Granular per-section update used by the Org Profile page
  const updateSection = useMutation({
    mutationFn: async (patch: Partial<OrgSettings>) => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data: existing } = await supabase
        .from("org_settings")
        .select("id, avg_annual_salary")
        .eq("user_id", user.id)
        .is("org_id", null)
        .maybeSingle();
      // Keep avg_hourly_rate in sync if salary is changing
      const finalPatch: Partial<OrgSettings> = { ...patch };
      if (patch.avg_annual_salary != null) {
        (finalPatch as any).avg_hourly_rate = deriveHourlyRate(patch.avg_annual_salary);
      }
      if (existing?.id) {
        const { error } = await supabase
          .from("org_settings")
          .update(finalPatch as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_settings").insert({
          user_id: user.id,
          org_id: null,
          ...(finalPatch as any),
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const skipOnboarding = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data: existing } = await supabase
        .from("org_settings")
        .select("id")
        .eq("user_id", user.id)
        .is("org_id", null)
        .maybeSingle();
      const stamp = new Date().toISOString();
      if (existing?.id) {
        const { error } = await supabase
          .from("org_settings")
          .update({ skipped_at: stamp, onboarding_dismissed_at: stamp })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_settings").insert({
          user_id: user.id,
          org_id: null,
          skipped_at: stamp,
          onboarding_dismissed_at: stamp,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-settings"] }),
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data: existing } = await supabase
        .from("org_settings")
        .select("id")
        .eq("user_id", user.id)
        .is("org_id", null)
        .maybeSingle();
      if (existing?.id) {
        const { error } = await supabase
          .from("org_settings")
          .update({ onboarding_dismissed_at: new Date().toISOString() })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_settings").insert({
          user_id: user.id,
          org_id: null,
          onboarding_dismissed_at: new Date().toISOString(),
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-settings"] });
    },
  });

  const recalibrate = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke(
        "calibrate-org-multipliers",
        { body: { org_id: null } },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-settings"] });
      qc.invalidateQueries({ queryKey: ["org-multipliers"] });
      qc.invalidateQueries({ queryKey: ["cycle-roi"] });
      toast.success("Multipliers recalibrated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    settings: data,
    isLoading,
    derivedHourlyRate: deriveHourlyRate(data?.avg_annual_salary ?? data?.avg_hourly_rate ? (data?.avg_annual_salary ?? null) : null),
    saveOnboarding: saveOnboarding.mutateAsync,
    isSaving: saveOnboarding.isPending,
    updateSection: updateSection.mutateAsync,
    isUpdatingSection: updateSection.isPending,
    skipOnboarding: skipOnboarding.mutateAsync,
    dismissBanner: dismiss.mutate,
    recalibrate: recalibrate.mutateAsync,
    isRecalibrating: recalibrate.isPending,
  };
}
